import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { QuestionnaireProvider } from './contexts/QuestionnaireContext.jsx';
import { AuthProvider, useAuth } from './contexts/AuthContext.jsx'; // Combined import
import { NotificationProvider } from './contexts/NotificationContext.jsx';

import OnboardingUserScreen from './screens/OnboardingScreen/OnboardingUserScreen.jsx';
import OnboardingAdminScreen from './screens/OnboardingScreen/OnboardingAdminScreen.jsx';
import OnboardingAgentScreen from './screens/OnboardingScreen/OnboardingAgentScreen.jsx';
import OnboardingSupplierScreen from './screens/OnboardingScreen/OnboardingSupplierScreen.jsx';

import LoginUserScreen from './screens/LoginScreen/LoginUserScreen';
import LoginAgentScreen from './screens/LoginScreen/LoginAgentScreen';
import LoginSupplierScreen from './screens/LoginScreen/LoginSupplierScreen.jsx';
import LoginAdminScreen from './screens/LoginScreen/LoginAdminScreen';
import RegisterUserScreen from './screens/RegisterScreen/RegisterUserScreen.jsx';
import RegisterSupplierScreen from './screens/RegisterScreen/RegisterSupplierScreen.jsx';
import RegisterAgentScreen from './screens/RegisterScreen/RegisterAgentScreen.jsx';
import RegisterAdminScreen from './screens/RegisterScreen/RegisterAdminScreen.jsx';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen/ForgotPasswordScreen';
import NewPasswordScreen from './screens/NewPasswordScreen/NewPasswordScreen';
import AuthPageLayout from './components/Layout/AuthPageLayout';
import TwoColumnAuthLayout from './components/Layout/TwoColumnAuthLayout';
import ProfileUserScreen from './screens/ProfileScreen/ProfileUserScreen.jsx';
import ProfileAdminScreen from './screens/ProfileScreen/ProfileAdminScreen.jsx';
import ProfileSupplierScreen from './screens/ProfileScreen/ProfileSupplierScreen.jsx';
import ProfileAgentScreen from './screens/ProfileScreen/ProfileAgentScreen.jsx';
import DashboardLayout from './components/Layout/DashboardLayout';
import QuestionPageLayout from './components/Layout/QuestionPageLayout';
import HomeUserScreen from './screens/HomeScreen/HomeUserScreen.jsx';
import HomeSupplierScreen from './screens/HomeScreen/HomeSupplierScreen.jsx';
import HomeAdminScreen from './screens/HomeScreen/HomeAdminScreen.jsx'; // Import Admin Home Screen
import ServiceFormSupplierScreen from './screens/ServiceScreen/ServiceFormSupplierScreen.jsx';
import ServiceListSupplierScreen from './screens/ServiceScreen/ServiceListSupplierScreen.jsx'; // Import the new list screen
import ServiceDetailSupplierScreen from './screens/ServiceScreen/ServiceDetailSupplierScreen.jsx'; // Import the detail screen
import UserListAdminScreen from './screens/UserScreen/UserListAdminScreen.jsx'; // Import Admin User List Screen
import UserFormAdminScreen from './screens/UserScreen/UserFormAdminScreen.jsx'; // Import Admin User Form Screen
import FAQManagerListAdminScreen from './screens/FAQManagerScreen/FAQManagerListAdminScreen.jsx'; // Import Admin FAQ List Screen
import ContractListAdminScreen from './screens/ContractScreen/ContractListAdminScreen.jsx'; // Import Contract List Admin Screen
import ContractFormAdminScreen from './screens/ContractScreen/ContractFormAdminScreen.jsx'; // Import Contract Form Admin Screen from './screens/ContractScreen/ContractFormAdminScreen.jsx';
import EvaluationListAdminScreen from './screens/EvaluationScreen/EvaluationListAdminScreen.jsx'; // Import Evaluation List Admin Screen
import FAQManagerFormAdminScreen from './screens/FAQManagerScreen/FAQManagerFormAdminScreen.jsx'; // Import Admin FAQ Form Screen
import HomeAgentScreen from './screens/HomeScreen/HomeAgentScreen.jsx'; // Import Home Agent Screen
import EvaluationViewAdminScreen from './screens/EvaluationScreen/EvaluationViewAdminScreen.jsx'; // Import Evaluation View Admin Screen
import QuizManagerAdminScreen from './screens/QuizManagerScreen/QuizManagerAdminScreen.jsx'; // Import Admin Quiz Manager Screen
import PaymentListAdminScreen from './screens/PaymentScreen/PaymentListAdminScreen.jsx'; // Import Payment List Admin Screen
import ChatAdminScreen from './screens/ChatScreen/ChatAdminScreen.jsx'; // Import Admin Chat Screen

import RequestListAgentScreen from './screens/RequestScreen/RequestListAgentScreen.jsx'; // Import Request List Agent Screen
import RequestViewScreen from './screens/RequestScreen/RequestViewScreen.jsx'; // Import Request View Screen
import ItineraryAgentScreen from './screens/ItineraryScreen/ItineraryAgentScreen.jsx'; // Import Itinerary Agent Screen
import SealSupplierScreen from './screens/SealScreen/SealSupplierScreen.jsx';
import SupplierVideoGalleryScreen from './screens/VideoGalleryScreen/SupplierVideoGalleryScreen.jsx';
import ItineraryListUserScreen from './screens/ItineraryScreen/ItineraryListUserScreen.jsx';
import ItineraryUserScreen from './screens/ItineraryScreen/ItineraryUserScreen.jsx';
import ItineraryGalleryUserScreen from './screens/ItineraryScreen/ItineraryGalleryUserScreen.jsx';
import FAQUserScreen from './screens/FAQScreen/FAQUserScreen.jsx';
import FAQSupplierScreen from './screens/FAQScreen/FAQSupplierScreen.jsx';
import FAQAgentScreen from './screens/FAQScreen/FAQAgentScreen.jsx';
import FAQAdminScreen from './screens/FAQScreen/FAQAdminScreen.jsx';
import NotificationUserScreen from './screens/NotificationScreen/NotificationUserScreen.jsx';
import NotificationSupplierScreen from './screens/NotificationScreen/NotificationSupplierScreen.jsx';
import NotificationAdminScreen from './screens/NotificationScreen/NotificationAdminScreen.jsx';
import NotificationAgentScreen from './screens/NotificationScreen/NotificationAgentScreen.jsx';
import FormSpinnerOverlay from './components/ui/FormSpinnerOverlay.jsx';

//import MockDynamicQuestionDriver from './screens/QuestionScreens/_BKP_DynamicQuestionDriver.jsx';
import DynamicQuestionDriver from './screens/QuestionScreens/DynamicQuestionDriver.jsx';

import './assets/css/App.css';
import './assets/css/React.css';
import './assets/js/scripts.js';

const UserWelcomeImageSide = () => <div className="col-md-6 image-side-welcome d-none d-md-block"></div>;
const UserLoginImageSide = () => <div className="col-md-6 image-side-login d-none d-md-block"></div>;

const SupplierWelcomeImageSide = () => <div className="col-md-6 image-side-welcome-supplier d-none d-md-block"></div>;
const SupplierLoginImageSide = () => <div className="col-md-6 image-side-login-supplier d-none d-md-block"></div>;

const AdminWelcomeImageSide = () => <div className="col-md-6 image-side-welcome-admin d-none d-md-block"></div>;
const AdminLoginImageSide = () => <div className="col-md-6 image-side-login-admin d-none d-md-block"></div>;

const AgentWelcomeImageSide = () => <div className="col-md-6 image-side-welcome-agent d-none d-md-block"></div>;
const AgentLoginImageSide = () => <div className="col-md-6 image-side-login-agent d-none d-md-block"></div>;

const ProtectedRoute = ({ children, requiredRole, allowedRoles, redirectTo = "/login" }) => {
    const { isAuthenticated, role: currentRole, isLoading } = useAuth();
    const location = useLocation();

    if (isLoading) {
        // Use your app's loading spinner, FormSpinnerOverlay is a good candidate
        return <FormSpinnerOverlay isLoading={true} message="Verificando sesión..." />;
    }

    if (!isAuthenticated) {
        // Preserve the attempted URL for redirect after login
        return <Navigate to={redirectTo} state={{ from: location }} replace />;
    }

    let roleMatch = false;
    if (requiredRole) {
        roleMatch = (currentRole === requiredRole);
    } else if (allowedRoles && Array.isArray(allowedRoles)) {
        roleMatch = allowedRoles.includes(currentRole);
    } else {
        // If neither requiredRole nor allowedRoles is provided,
        // then just being authenticated is enough.
        roleMatch = true;
    }

    if (!roleMatch) {
        // If authenticated but role doesn't match, redirect.
        return <Navigate to={redirectTo} replace />;
    }

    return children;
};
function App() {
    const [isAppShellLoading, setIsAppShellLoading] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsAppShellLoading(false);
        }, 250);
        return () => clearTimeout(timer);
    }, []);
    // Removed: userRoleForRoutes and isAuthenticatedForRoutes
    // These will now be handled by ProtectedRoute using AuthContext

    const initialAppMode = process.env.REACT_APP_USER_ROLE || 'user';
    let LoginComponentToRender;
    let OnboardingComponentToRender;
    let ProfileComponentToRender;
    let HomeComponentToRender;
    let FAQComponentToRender;
    let WelcomeImageComponentToRender;
    let LoginImageComponentToRender;
    let NotificationComponentToRender;
    let RegisterComponentToRender;



    switch (initialAppMode) {
        case 'admin':
            LoginComponentToRender = LoginAdminScreen;
            OnboardingComponentToRender = OnboardingAdminScreen;
            ProfileComponentToRender = ProfileAdminScreen; // Use ProfileAdminScreen for admins
            WelcomeImageComponentToRender = AdminWelcomeImageSide;
            LoginImageComponentToRender = AdminLoginImageSide;
            HomeComponentToRender = HomeAdminScreen; // Assign Admin Home Screen
            FAQComponentToRender = FAQAdminScreen;
            NotificationComponentToRender = NotificationAdminScreen;
            RegisterComponentToRender = RegisterAdminScreen;
            break;
        case 'agent':
            LoginComponentToRender = LoginAgentScreen;
            OnboardingComponentToRender = OnboardingAgentScreen;
            ProfileComponentToRender = ProfileAgentScreen;
            WelcomeImageComponentToRender = AgentWelcomeImageSide;
            LoginImageComponentToRender = AgentLoginImageSide;
            HomeComponentToRender = HomeAgentScreen; // Assign Agent Home Screen
            FAQComponentToRender = FAQAgentScreen;
            NotificationComponentToRender = NotificationAgentScreen;
            RegisterComponentToRender = RegisterAgentScreen;
            break;
        case 'supplier':
            LoginComponentToRender = LoginSupplierScreen;
            OnboardingComponentToRender = OnboardingSupplierScreen;
            ProfileComponentToRender = ProfileSupplierScreen;
            WelcomeImageComponentToRender = SupplierWelcomeImageSide;
            LoginImageComponentToRender = SupplierLoginImageSide;
            HomeComponentToRender = HomeSupplierScreen;
            FAQComponentToRender = FAQSupplierScreen;
            NotificationComponentToRender = NotificationSupplierScreen;
            RegisterComponentToRender = RegisterSupplierScreen;
            break;
        case 'user':
        default:
            LoginComponentToRender = LoginUserScreen;
            OnboardingComponentToRender = OnboardingUserScreen;
            ProfileComponentToRender = ProfileUserScreen;
            WelcomeImageComponentToRender = UserWelcomeImageSide;
            LoginImageComponentToRender = UserLoginImageSide;
            HomeComponentToRender = HomeUserScreen;
            FAQComponentToRender = FAQUserScreen;
            NotificationComponentToRender = NotificationUserScreen;
            RegisterComponentToRender = RegisterUserScreen;
            break;
    }

    if (isAppShellLoading) {
        return <FormSpinnerOverlay isLoading={isAppShellLoading} />;
    }

    return (
        <Router>
            <AuthProvider>
                <NotificationProvider>
                    <QuestionnaireProvider>
                        <div className="App">
                            <main className="app-content">
                                <Routes>
                                    <Route
                                        path="/"
                                        element={
                                            <TwoColumnAuthLayout
                                                ImageComponent={WelcomeImageComponentToRender}
                                                FormComponent={OnboardingComponentToRender}
                                            />
                                        }
                                    />
                                    <Route
                                        path="/login"
                                        element={
                                            <TwoColumnAuthLayout
                                                ImageComponent={LoginImageComponentToRender}
                                                FormComponent={LoginComponentToRender}
                                            />
                                        }
                                    />
                                    <Route path="/register" element={
                                        <AuthPageLayout>
                                            <RegisterComponentToRender />
                                        </AuthPageLayout>
                                    } />


                                    <Route path="/forgot-password" element={
                                        <AuthPageLayout><ForgotPasswordScreen /></AuthPageLayout>
                                    } />
                                    <Route path="/new-password" element={
                                        <AuthPageLayout><NewPasswordScreen /></AuthPageLayout>
                                    } />
                                    <Route
                                        path="/questions/:stepKeyParam"
                                        element={
                                            <QuestionPageLayout>
                                                <DynamicQuestionDriver baseRoute="/questions/" isScrollable={true} targetQuizName={"Outer Questionnaire"} expectedRole="user" />
                                            </QuestionPageLayout>
                                        } />
                                    <Route path="/questions" element={<Navigate to="/questions/step1_1" replace />} />
                                    <Route
                                        path="/user/questions/:stepKeyParam"
                                        element={
                                            <QuestionPageLayout>
                                                <DynamicQuestionDriver baseRoute="/user/questions/" isScrollable={true} targetQuizName={"Inner Questionnaire"} expectedRole="user" />
                                            </QuestionPageLayout>
                                        } />
                                    <Route path="/user/questions" element={<Navigate to="/user/questions/step1_1_clone_20_1750374165601_9w0xe" replace />} />
                                    <Route
                                        path="/supplier/questions/:stepKeyParam"
                                        element={
                                            <QuestionPageLayout>
                                                <DynamicQuestionDriver baseRoute="/supplier/questions/" isScrollable={true} targetQuizName={"Sello Checkin"} expectedRole="supplier" />
                                            </QuestionPageLayout>
                                        } />
                                    <Route path="/supplier/questions" element={<Navigate to="/supplier/questions/pstep1_1" replace />} />

                                    <Route
                                        path="/supplier/questions-pe/:stepKeyParam"
                                        element={
                                            <QuestionPageLayout>
                                                <DynamicQuestionDriver baseRoute="/supplier/questions-pe/" isScrollable={true} targetQuizName={"Pre evaluación"} expectedRole="supplier" />
                                            </QuestionPageLayout>
                                        } />
                                    <Route path="/supplier/questions-pe/" element={<Navigate to="/supplier/questions-pe/pestep1_1" replace />} />


                                    <Route path="/home" element={
                                        <ProtectedRoute requiredRole={initialAppMode} redirectTo="/login">
                                            {HomeComponentToRender ? <HomeComponentToRender /> : <Navigate to="/login" />}
                                        </ProtectedRoute>
                                    } />

                                    <Route path="/profile" element={
                                        <ProtectedRoute requiredRole={initialAppMode} redirectTo="/login">
                                            {ProfileComponentToRender ? <ProfileComponentToRender /> : <Navigate to="/login" />}
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/faq" element={
                                        <ProtectedRoute allowedRoles={['user', 'agent', 'supplier', 'admin']} redirectTo="/login">
                                            <DashboardLayout activeMenuItem="Ayuda">
                                                <FAQComponentToRender />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/itinerary" element={
                                        <ProtectedRoute requiredRole="user" redirectTo="/login">
                                            <ItineraryListUserScreen />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/itinerary/:id" element={
                                        <ProtectedRoute requiredRole="user" redirectTo="/login">
                                            <ItineraryUserScreen />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/itinerary/:id/gallery" element={
                                        <ProtectedRoute requiredRole="user" redirectTo="/login">
                                            <ItineraryGalleryUserScreen />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/notifications" element={
                                        <ProtectedRoute allowedRoles={['user', 'agent', 'supplier', 'admin']} redirectTo="/login">
                                            <NotificationComponentToRender />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/dashboard-seal" element={
                                        <ProtectedRoute requiredRole="supplier" redirectTo="/login">
                                            <DashboardLayout activeMenuItem="Sello Checkin">
                                                <SealSupplierScreen />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/training" element={
                                        <ProtectedRoute requiredRole="supplier" redirectTo="/login">
                                            <SupplierVideoGalleryScreen />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/users" element={
                                        <ProtectedRoute requiredRole="admin" redirectTo="/login">
                                            <DashboardLayout activeMenuItem="Usuarios" isScrollable={true}>
                                                <UserListAdminScreen />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/evaluations" element={
                                        <ProtectedRoute requiredRole="admin" redirectTo="/login">
                                            <DashboardLayout activeMenuItem="Evaluaciones" isScrollable={true}>
                                                <EvaluationListAdminScreen />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/evaluations/:evaluationId" element={
                                        <ProtectedRoute requiredRole="admin" redirectTo="/login">
                                            <DashboardLayout activeMenuItem="Evaluaciones" isScrollable={true}>
                                                <EvaluationViewAdminScreen />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/services" element={
                                        <ProtectedRoute requiredRole="supplier" redirectTo="/login">
                                            <DashboardLayout activeMenuItem="Mis Servicios" isScrollable={true}>
                                                <ServiceListSupplierScreen />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/services/add" element={
                                        <ProtectedRoute requiredRole="supplier" redirectTo="/login">
                                            <ServiceFormSupplierScreen />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/services/edit/:serviceId" element={
                                        <ProtectedRoute requiredRole="supplier" redirectTo="/login">
                                            <ServiceFormSupplierScreen />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/services/detail/:serviceId" element={
                                        <ProtectedRoute requiredRole="supplier" redirectTo="/login">
                                            <DashboardLayout activeMenuItem="Mis Servicios" isScrollable={true}>
                                                <ServiceDetailSupplierScreen />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/users/edit/:baseRoleKeyParam/:userIdParam" element={
                                        <ProtectedRoute requiredRole="admin" redirectTo="/login">
                                            {/* DashboardLayout is handled within UserFormAdminScreen */}
                                            <UserFormAdminScreen />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/contracts" element={
                                        <ProtectedRoute requiredRole="admin" redirectTo="/login">
                                            <DashboardLayout activeMenuItem="Contratos" isScrollable={true}>
                                                <ContractListAdminScreen />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/contracts/new" element={
                                        <ProtectedRoute requiredRole="admin" redirectTo="/login">
                                            <DashboardLayout activeMenuItem="Contratos" isScrollable={true}>
                                                <ContractFormAdminScreen />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/contracts/edit/:contractId" element={
                                        <ProtectedRoute requiredRole="admin" redirectTo="/login">
                                            <DashboardLayout activeMenuItem="Contratos" isScrollable={true}>
                                                <ContractFormAdminScreen />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                    {/* Renamed from /admin/faqs to /settings/faqs to match existing pattern, and changed component */}
                                    <Route path="/settings/faqs" element={
                                        <ProtectedRoute requiredRole="admin" redirectTo="/login">
                                            <DashboardLayout activeMenuItem="FAQs" isScrollable={true}>
                                                <FAQManagerListAdminScreen />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/settings/faqs/add" element={
                                        <ProtectedRoute requiredRole="admin" redirectTo="/login">
                                            <DashboardLayout activeMenuItem="FAQs" isScrollable={true}>
                                                <FAQManagerFormAdminScreen />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/settings/faqs/edit/:faqId" element={
                                        <ProtectedRoute requiredRole="admin" redirectTo="/login">
                                            <DashboardLayout activeMenuItem="FAQs" isScrollable={true}>
                                                <FAQManagerFormAdminScreen />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/payments" element={
                                        <ProtectedRoute requiredRole="admin" redirectTo="/login">
                                            <DashboardLayout activeMenuItem="Estados de Pago" isScrollable={true}>
                                                <PaymentListAdminScreen />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                    {/* Ensure this catch-all or specific /admin/users route is distinct or ordered correctly if needed */}
                                    <Route path="/users" element={
                                        <ProtectedRoute requiredRole="admin" redirectTo="/login">
                                            <DashboardLayout activeMenuItem="Usuarios" isScrollable={true}>
                                                <UserListAdminScreen />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/settings/quiz-manager" element={
                                        <ProtectedRoute requiredRole="admin" redirectTo="/login">
                                            <DashboardLayout activeMenuItem="Quiz Manager" isScrollable={true}>
                                                <QuizManagerAdminScreen />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/chat" element={
                                        <ProtectedRoute requiredRole="admin" redirectTo="/login">
                                            <DashboardLayout activeMenuItem="Chat" isScrollable={true}>
                                                <ChatAdminScreen />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/requests" element={
                                        <ProtectedRoute requiredRole="agent" redirectTo="/login">
                                            <DashboardLayout activeMenuItem="Solicitudes" isScrollable={true}>
                                                <RequestListAgentScreen />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/requests/:id" element={
                                        <ProtectedRoute requiredRole="agent" redirectTo="/login">
                                            <DashboardLayout activeMenuItem="Solicitudes" isScrollable={true}>
                                                <ItineraryAgentScreen />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/requests/:id/view" element={
                                        <ProtectedRoute requiredRole="agent" redirectTo="/login">
                                            <DashboardLayout activeMenuItem="Solicitudes" isScrollable={true}>
                                                <RequestViewScreen />
                                            </DashboardLayout>
                                        </ProtectedRoute>
                                    } />
                                </Routes>
                            </main>
                        </div>
                    </QuestionnaireProvider>
                </NotificationProvider>
            </AuthProvider>
        </Router>
    );
}

export default App;
